import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Loader2, DollarSign, Coins, Users, Clock, CheckCircle2, Wallet, Mail,
} from "lucide-react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/payouts"),
      api.get("/admin/transactions"),
    ])
      .then(([s, p, t]) => {
        setStats(s.data);
        setPayouts(p.data);
        setTxns(t.data);
      })
      .catch(() => toast.error("Could not load admin data"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markPaid = async (id) => {
    try {
      await api.post(`/admin/payouts/${id}/mark-paid`);
      toast.success("Marked as paid");
      load();
    } catch {
      toast.error("Could not update");
    }
  };

  const [sending, setSending] = useState(false);
  const sendSummary = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/send-summary");
      toast.success(data.sent ? "Summary emailed to you!" : "Email not configured — set SendGrid keys");
    } catch {
      toast.error("Could not send summary");
    } finally {
      setSending(false);
    }
  };

  if (loading)
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#050505]">
        <Loader2 className="h-8 w-8 animate-spin text-[#00E676]" />
      </div>
    );

  if (!stats)
    return (
      <div className="App min-h-screen bg-[#050505]">
        <Navbar />
        <div className="mx-auto max-w-lg px-5 py-32 text-center">
          <h1 className="font-display text-3xl font-black tracking-tight">Couldn't load admin data</h1>
          <button
            data-testid="admin-retry-btn"
            onClick={() => {
              setLoading(true);
              load();
            }}
            className="mt-6 rounded-full bg-[#00E676] px-6 py-3 font-bold text-black active:scale-95"
          >
            Retry
          </button>
        </div>
      </div>
    );

  const cards = [
    { icon: Coins, label: "Fees collected (3%)", value: `$${stats.total_fees_collected.toFixed(2)}`, testid: "admin-fees" },
    { icon: DollarSign, label: "Tips processed", value: `$${stats.total_tips_processed.toFixed(2)}`, testid: "admin-tips" },
    { icon: Users, label: "Recipients", value: stats.recipient_count, testid: "admin-recipients" },
    { icon: Wallet, label: "Pending payouts", value: `$${stats.pending_payout_amount.toFixed(2)}`, testid: "admin-pending" },
  ];

  return (
    <div className="App min-h-screen bg-[#050505]">
      <Navbar />
      <div className="mx-auto max-w-6xl px-5 py-10">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Admin</p>
          <div className="mt-2 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <h1 className="font-display text-4xl font-black tracking-tight">Platform earnings</h1>
            <button
              data-testid="send-summary-btn"
              onClick={sendSummary}
              disabled={sending}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-white/5 disabled:opacity-60"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4 text-[#00E676]" />}
              Email me this week's summary
            </button>
          </div>
          <p className="mt-2 text-sm text-zinc-500">A summary is also emailed automatically every Monday.</p>
        </motion.div>

        {/* Stat cards */}
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {cards.map((c) => (
            <div key={c.label} className="rounded-2xl border border-white/10 bg-[#0F0F0F] p-6">
              <c.icon className="h-6 w-6 text-[#00E676]" />
              <div data-testid={c.testid} className="mt-4 font-display text-3xl font-black">
                {c.value}
              </div>
              <p className="mt-1 text-sm text-zinc-500">{c.label}</p>
            </div>
          ))}
        </div>

        {/* Payout requests */}
        <div className="mt-8 rounded-2xl border border-white/10 bg-[#0F0F0F] p-6 sm:p-8">
          <h2 className="font-display text-xl font-semibold">Payout requests</h2>
          {payouts.length === 0 ? (
            <p className="mt-4 text-zinc-500">No payout requests yet.</p>
          ) : (
            <div className="mt-6 space-y-2" data-testid="admin-payouts-list">
              {payouts.map((p) => (
                <div
                  key={p.id}
                  className="flex flex-col gap-3 rounded-xl border border-white/10 bg-[#050505] px-4 py-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div>
                    <div className="font-semibold">
                      {p.recipient_name}{" "}
                      <span className="font-mono-tip text-sm text-zinc-500">@{p.recipient_username}</span>
                    </div>
                    <div className="mt-1 text-sm text-zinc-400">
                      Send{" "}
                      <span className="font-bold text-[#00E676]">${p.amount.toFixed(2)}</span> via{" "}
                      <span className="capitalize">{p.method}</span> to{" "}
                      <span className="font-mono-tip text-white">{p.destination}</span>
                    </div>
                  </div>
                  {p.status === "paid" ? (
                    <span className="flex items-center gap-1.5 text-sm font-semibold text-[#00E676]">
                      <CheckCircle2 className="h-4 w-4" /> Paid
                    </span>
                  ) : (
                    <button
                      data-testid={`mark-paid-${p.id}`}
                      onClick={() => markPaid(p.id)}
                      className="flex items-center gap-2 rounded-full bg-[#00E676] px-5 py-2.5 text-sm font-bold text-black active:scale-95"
                    >
                      <Clock className="h-4 w-4" /> Mark paid
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Transactions */}
        <div className="mt-8 rounded-2xl border border-white/10 bg-[#0F0F0F] p-6 sm:p-8">
          <h2 className="font-display text-xl font-semibold">Recent tips</h2>
          {txns.length === 0 ? (
            <p className="mt-4 text-zinc-500">No tips processed yet.</p>
          ) : (
            <div className="mt-6 overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-xs uppercase tracking-[0.15em] text-zinc-500">
                  <tr>
                    <th className="pb-3">Recipient</th>
                    <th className="pb-3">Tip</th>
                    <th className="pb-3">Fee (3%)</th>
                    <th className="pb-3">Total</th>
                  </tr>
                </thead>
                <tbody data-testid="admin-txns">
                  {txns.map((t, i) => (
                    <tr key={`${t.created_at}-${t.recipient_username}-${i}`} className="border-t border-white/5">
                      <td className="py-3">
                        {t.recipient_name}{" "}
                        <span className="font-mono-tip text-zinc-500">@{t.recipient_username}</span>
                      </td>
                      <td className="py-3 font-mono-tip">${t.tip_amount.toFixed(2)}</td>
                      <td className="py-3 font-mono-tip text-[#00E676]">${t.service_fee.toFixed(2)}</td>
                      <td className="py-3 font-mono-tip">${t.amount_total.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
